<script lang="ts">
  import PageMeta from '$lib/components/PageMeta.svelte';
  import GridLines from '$lib/components/GridLines.svelte';
  import GridContent from '$lib/components/GridContent.svelte';
  import GridInner from '$lib/components/GridInner.svelte';
  import CornerFrame from '$lib/components/CornerFrame.svelte';
  import Star from '$lib/components/Star.svelte';
  import ContactCtaSection from '$lib/sections/ContactCtaSection.svelte';
  import { hatchStyle45 } from '$lib/styles/hatch';
  import { Settings, FlaskConical, ShieldCheck, Rocket, Zap, Brain, User } from '@lucide/svelte';

  // Builder workflow steps
  const workflowSteps = [
    {
      step: '01',
      label: 'Configure',
      description: 'Visual agent configuration — scope, model selection, permission boundaries, connected data sources.',
      icon: Settings
    },
    {
      step: '02',
      label: 'Test',
      description: 'Sandbox testing with debugging tools. Iterate until it works the way your business needs it to.',
      icon: FlaskConical
    },
    {
      step: '03',
      label: 'Approve',
      description: 'Publish flow with IT/admin approval gates. Security team has visibility before anything goes live.',
      icon: ShieldCheck
    },
    {
      step: '04',
      label: 'Ship',
      description: 'Deploy to any surface — Hub, widget, Teams, Slack, API. One agent, many entry points.',
      icon: Rocket
    }
  ];

  // Agent archetypes (from existing agents page)
  const agentTypes = [
    {
      type: 'Agents',
      headline: 'AI that gets things done.',
      description: 'Domain-focused, task-driven. Handle specific jobs with defined scope and clear boundaries.',
      useCases: ['Customer support', 'IT help desk', 'HR policy', 'Sales enablement'],
      icon: Zap
    },
    {
      type: 'Copilots',
      headline: 'AI that thinks alongside you.',
      description: 'Broad scope, conversational. Pull from knowledge bases, reason through problems, help you work through complexity.',
      useCases: ['Research & analysis', 'Document drafting', 'Strategy exploration', 'Onboarding'],
      icon: Brain
    },
    {
      type: 'Assistants',
      headline: 'AI that knows you.',
      description: 'Personal, persistent, proactive. Learn preferences, manage context, orchestrate other agents on your behalf.',
      useCases: ['Executive assistant', 'Personal productivity', 'Cross-functional coordination'],
      icon: User
    }
  ];
</script>

<PageMeta
  title="Agent Builder — Build agents your CISO will approve"
  description="Design AI agents for your specific workflows. Define permissions, test in sandbox, promote through approval flows. No code required — fully extensible when you need it."
/>

<!-- Top stars -->
<div class="relative">
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] -top-px" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] -top-px" />
</div>

<!-- Hero -->
<section class="bg-[#F9F9F4] py-[80px] lg:py-[120px] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <GridContent>
    <GridInner>
      <div class="max-w-[700px]">
        <h1 class="font-sans text-[48px] lg:text-[64px] tracking-[-0.04em] leading-[1.1] text-[#020202] mb-6">
          Build agents your CISO will approve.
        </h1>
        <p class="font-mono font-light text-[16px] text-[#5C5855] leading-relaxed">
          Design AI agents for your specific workflows. Define what they can access, who can use them, and what actions they're allowed to take. Test in a sandboxed environment. Promote to production through an approval flow that gives IT visibility and control.
        </p>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider -->
<div class="h-[48px] bg-[#f9f9f4] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <div class="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] bottom-[-9px]" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] bottom-[-9px]" />
</div>

<!-- §1: Workflow stepper -->
<section class="bg-[#0d0d0d] py-[80px] relative">
  <GridLines variant="dark" />
  <GridContent>
    <GridInner>
      <div class="max-w-[700px] mx-auto mb-12">
        <h2 class="font-sans text-[36px] lg:text-[48px] text-white tracking-[-0.04em] leading-[1.1] mb-6">
          From idea to production. With guardrails.
        </h2>
        <p class="font-body font-light text-[16px] text-[#878787] leading-relaxed">
          No code required for standard agents — but Corral is fully extensible for teams that want to go deeper. API-first, MCP and OpenAPI extension points, custom tool definitions. The foundation gets out of your way when you need it to.
        </p>
      </div>

      <!-- Steps -->
      <div class="relative">
        <div class="hidden lg:block absolute top-0 -left-[calc(54px+9.03vw)] -right-[calc(54px+9.03vw)] h-px bg-[#333335] z-[5]" aria-hidden="true"></div>
        <Star variant="full" class="hidden lg:block absolute -left-[63px] top-[-9px]" />
        <Star variant="full" class="hidden lg:block absolute -right-[63px] top-[-9px]" />

        <div class="hidden lg:block absolute bottom-0 -left-[calc(54px+9.03vw)] -right-[calc(54px+9.03vw)] h-px bg-[#333335] z-[5]" aria-hidden="true"></div>
        <Star variant="full" class="hidden lg:block absolute -left-[63px] bottom-[-9px]" />
        <Star variant="full" class="hidden lg:block absolute -right-[63px] bottom-[-9px]" />

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0">
          {#each workflowSteps as step, i}
            <div class="bg-[#171719] border border-[#333335] p-6 {i > 0 ? 'lg:-ml-px' : ''} {i >= 2 ? '-mt-px lg:mt-0' : ''}">
              <div class="flex items-center gap-3 mb-4">
                <CornerFrame
                  class="w-[36px] h-[36px] flex items-center justify-center"
                  bg="bg-transparent"
                  borderColor="rgba(255, 255, 255, 0.2)"
                  cornerColor="white"
                  cornerSize={6}
                >
                  <svelte:component this={step.icon} class="w-4 h-4 text-white" />
                </CornerFrame>
                <span class="font-mono text-[12px] text-[#ee6018]">{step.step}</span>
              </div>
              <h3 class="font-sans text-[18px] text-white mb-3">{step.label}</h3>
              <p class="font-mono font-light text-[13px] text-[#878787] leading-relaxed">{step.description}</p>
            </div>
          {/each}
        </div>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider -->
<div class="h-[48px] bg-[#f9f9f4] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <div class="hidden lg:block absolute top-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] top-0" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] top-0" />
</div>

<!-- §2: What you can build — Agent types -->
<section class="bg-[#f9f9f4] py-[80px] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <GridContent>
    <GridInner>
      <div class="text-center mb-12">
        <h2 class="font-sans text-[36px] lg:text-[48px] text-[#020202] tracking-[-0.04em] leading-[1.1] mb-4">
          Same platform. Different configurations.
        </h2>
        <p class="font-mono font-light text-[16px] text-[#5C5855] max-w-[600px] mx-auto">
          Agents, Copilots, and Assistants aren't different products — they're different patterns. Start with an Agent. Expand to Copilots. Graduate to Assistants.
        </p>
      </div>

      <div class="relative">
        <div class="hidden lg:block absolute top-0 -left-[calc(54px+9.03vw)] -right-[calc(54px+9.03vw)] h-px bg-[#D3D3D3] z-[5]" aria-hidden="true"></div>
        <Star variant="full" color="#D3D3D3" class="hidden lg:block absolute -left-[63px] top-[-9px]" />
        <Star variant="full" color="#D3D3D3" class="hidden lg:block absolute -right-[63px] top-[-9px]" />

        <div class="hidden lg:block absolute bottom-0 -left-[calc(54px+9.03vw)] -right-[calc(54px+9.03vw)] h-px bg-[#D3D3D3] z-[5]" aria-hidden="true"></div>
        <Star variant="full" color="#D3D3D3" class="hidden lg:block absolute -left-[63px] bottom-[-9px]" />
        <Star variant="full" color="#D3D3D3" class="hidden lg:block absolute -right-[63px] bottom-[-9px]" />

        <div class="grid grid-cols-1 md:grid-cols-3 gap-0">
          {#each agentTypes as agent, i}
            <div class="bg-white border border-[#D3D3D3] p-8 {i > 0 ? 'lg:-ml-px' : ''} {i >= 1 ? '-mt-px lg:mt-0' : ''}">
              <div class="flex items-center gap-3 mb-4">
                <CornerFrame
                  class="w-[43px] h-[44px] flex items-center justify-center"
                  bg="bg-transparent"
                  borderColor="rgba(0, 0, 0, 0.15)"
                  cornerColor="#020202"
                  cornerSize={7}
                >
                  <svelte:component this={agent.icon} class="w-4 h-4 text-[#020202]" />
                </CornerFrame>
                <span class="font-mono text-[12px] text-[#ee6018] uppercase tracking-wider">{agent.type}</span>
              </div>
              <h3 class="font-sans text-[18px] text-[#020202] mb-2">{agent.headline}</h3>
              <p class="font-mono font-light text-[13px] text-[#5C5855] leading-relaxed mb-5">
                {agent.description}
              </p>
              <ul class="flex flex-col gap-2">
                {#each agent.useCases as useCase}
                  <li class="font-mono font-light text-[12px] text-[#020202] flex items-center gap-2">
                    <span class="w-1 h-1 bg-[#ee6018] rounded-full shrink-0"></span>
                    {useCase}
                  </li>
                {/each}
              </ul>
            </div>
          {/each}
        </div>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider -->
<div class="h-[48px] bg-[#0d0d0d] relative">
  <GridLines variant="dark" />
  <div class="hidden lg:block absolute top-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] top-0" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] top-0" />
</div>

<!-- §3: Extensibility callout -->
<section class="bg-[#0d0d0d] py-[80px] relative">
  <GridLines variant="dark" />
  <GridContent>
    <GridInner>
      <div class="max-w-[700px] mx-auto">
        <div class="border-l-2 border-[#ee6018] pl-6 py-2 mb-8">
          <p class="font-mono font-light text-[18px] text-white leading-relaxed">
            The foundation gets out of your way. API-first, MCP and OpenAPI extension points, custom tool definitions. Build on top of it. Override pieces of it. Extend it with your own business logic.
          </p>
        </div>
        <p class="font-body font-light text-[16px] text-[#878787] leading-relaxed">
          Pre-built templates and starter agents get you moving fast. Custom tool definitions and business logic let you go as deep as you want. Every agent inherits the security model — CRF governs across everything you build.
        </p>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider: → CTA -->
<div class="h-[48px] bg-[#0d0d0d] relative">
  <GridLines variant="dark" />
  <div class="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] bottom-[-9px]" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] bottom-[-9px]" />
</div>

<!-- CTA -->
<ContactCtaSection />
